import angular from 'angular';

import config from '../../config';

import _cdnResizeUrl from './cdnResizeUrl';
import _isCdnResizeEnabled from './isCdnResizeEnabled';

export default function AttachmentUtils($q) {
  'ngInject';

  const service = this;
  service.videoAspectRatio = 16 / 9;

  // SLING-978: video thumbnails come straight from the providers instead of
  // Cloudinary — these sources are correct regardless of the CDN-resize flag,
  // so no flag gate is needed here.
  function _youtubeThumbUrl(videoId) {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }

  function _storedVideoThumb(attachment) {
    return attachment.image || attachment.url;
  }

  function _youtubeParser(url) {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*)(?:[#&?]t=([^#&?]*))?.*/;
    const match = url.match(regExp);
    const result = {};
    if (match && match[2].length === 11) {
      result.src = match[2];
    }
    if (match && match[3]) {
      const timeArray = match[3].split('m');
      if (timeArray.length === 2) {
        result.start =
          Number(timeArray[0].replace(/\D/g, '')) * 60 + Number(timeArray[1].replace(/\D/g, ''));
      } else {
        result.start = Number(match[3]);
      }
    }
    return result.src ? result : null;
  }

  function _vimeoParser(url) {
    const regExp = /(www\.)?vimeo.com\/(?:.*\/)?(\d+)($|\/)/;
    const match = url.match(regExp);
    if (match) {
      return match[2];
    }
  }

  function _vineParser(url) {
    const regExp = /https?:\/\/(www\.)?vine.co\/v\/(\w+)($|\/)/;
    const match = url.match(regExp);
    if (match) {
      return match[2];
    }
  }

  // SLING-791: Build a CDN resize URL for an S3-hosted image.
  // The S3 key is extracted from the full S3/CloudFront URL and rewritten as a
  // CDN resize URL so the Lambda@Edge function handles resizing on cache miss.
  // Non-S3 URLs (external images) fall back to the original URL unchanged.
  // Flag-OFF path — Cloudinary remains the resize backend until
  // config.CDN_RESIZE_ENABLED is turned on in a deploy.
  function _cloudinaryFetchUrl(url, transformations) {
    return `${config.CLOUDINARY_BASE_URL}image/fetch/${transformations}/${encodeURIComponent(url)}`;
  }

  function _setImageResizeUrls(attachment) {
    const cdnEnabled = _isCdnResizeEnabled();
    if (cdnEnabled) {
      attachment.large = _cdnResizeUrl(attachment.url, 1000, 1000, 'inside');
      attachment.thumb = _cdnResizeUrl(attachment.url, attachment.thumbWidth, attachment.thumbHeight, 'cover');
    } else {
      attachment.large = _cloudinaryFetchUrl(attachment.url, 'f_jpg,c_limit,h_1000,w_1000');
      attachment.thumb = _cloudinaryFetchUrl(
        attachment.url,
        `f_jpg,c_${attachment.thumbMode},h_${attachment.thumbHeight},w_${attachment.thumbWidth},b_rgb:ffffff`
      );
    }
    return cdnEnabled;
  }

  // SLING-794 (Option B): When dimensions are not available on the attachment
  // object, read them from the x-image-width/x-image-height response headers
  // the CDN sets on resize URLs (origin-response Lambda + the
  // sling-image-resize-headers policy expose them cross-origin). The fetch
  // shares the browser HTTP cache with the <img> that renders the same URL,
  // and the body is cancelled once the headers are read. For images without
  // the headers (non-CDN URLs, variants predating the origin-response
  // deploy), fall back to loading the image and measuring it.
  async function _fetchDimensionHeaders(url) {
    try {
      const response = await fetch(url);
      const dimensions = service._parseDimensionHeaders(response.headers);
      if (response.body && response.body.cancel) {
        response.body.cancel();
      }
      return dimensions;
    } catch (e) {
      // Network/CORS failure — fall back to measuring.
      return null;
    }
  }

  // The probe must use the same backend the flag selected: with CDN resize
  // off, probing the (not yet live) CDN URL would 404 and leave width/height
  // unset, so fall back to a low-quality Cloudinary fetch as on develop.
  async function _getImageAttachmentSize(attachment, largeSize, cdnEnabled) {
    if (!__TEST__ && (!attachment.width || !attachment.height)) {
      let probeUrl = attachment.url;
      if (largeSize) {
        probeUrl = cdnEnabled
          ? _cdnResizeUrl(attachment.url, largeSize, largeSize, 'inside')
          : _cloudinaryFetchUrl(attachment.url, `f_jpg,c_limit,h_${largeSize},w_${largeSize},q_1`);
      }

      if (probeUrl.indexOf(`https://${config.SLING_UPLOADS}/`) === 0) {
        const dimensions = await _fetchDimensionHeaders(probeUrl);
        if (dimensions) {
          attachment.width = dimensions.width;
          attachment.height = dimensions.height;
          return attachment;
        }
      }

      return await $q((resolve, reject) => {
        const tempImage = new Image();

        tempImage.onload = function tempImageOnload() {
          attachment.width = tempImage.naturalWidth;
          attachment.height = tempImage.naturalHeight;
          resolve(attachment);
        };

        tempImage.onerror = reject;
        tempImage.src = probeUrl;
      });
    }

    return attachment;
  }

  // Parse x-image-width/x-image-height from a fetch Headers object. Returns
  // {width, height} only when both are positive integers, else null (the
  // caller falls back to measuring). Exposed on the service for unit tests.
  service._parseDimensionHeaders = function _parseDimensionHeaders(headers) {
    const width = parseInt(headers.get('x-image-width'), 10);
    const height = parseInt(headers.get('x-image-height'), 10);
    if (width > 0 && height > 0) {
      return { width, height };
    }
    return null;
  };

  async function _getEffectiveSize(attachment) {
    if (!__TEST__ && !attachment.effectiveHeight) {
      return await $q((resolve, reject) => {
        const tempImage = new Image();

        tempImage.onload = function tempImageOnload() {
          const nw = tempImage.naturalWidth;
          const nh = tempImage.naturalHeight;
          const tw = attachment.thumbWidth;
          const th = attachment.thumbHeight;
          const ratio = nw / nh;
          if (nw <= tw && nh <= th) {
            attachment.effectiveHeight = nh;
            attachment.effectiveWidth = nw;
          } else if (th * ratio <= tw) {
            attachment.effectiveHeight = th;
            attachment.effectiveWidth = th * ratio;
          } else {
            attachment.effectiveHeight = tw / ratio;
            attachment.effectiveWidth = tw;
          }
          resolve(attachment);
        };

        tempImage.onerror = reject;
        tempImage.src = attachment.thumb;
      });
    }

    return attachment;
  }

  function _getYoutubeEmbed(url, vidWidth, vidHeight) {
    const parsed = _youtubeParser(url);
    if (parsed) {
      return `<iframe width="${vidWidth}" height="${vidHeight}" frameborder="0" scrolling="no" allowtransparency="true" webkitallowfullscreen mozallowfullscreen allowfullscreen="true" src="//www.youtube.com/embed/${
        parsed.src
      }?rel=0&wmode=transparent&showinfo=0&autoplay=1${
        'start' in parsed ? `&start=${parsed.start}` : ''
      }"></iframe>`;
    }
  }

  function _getVimeoEmbed(url, vidWidth, vidHeight) {
    const parsed = _vimeoParser(url);
    if (parsed) {
      return `<iframe width="${vidWidth}" height="${vidHeight}" frameborder="0" scrolling="no" allowtransparency="true" webkitallowfullscreen mozallowfullscreen allowfullscreen="true" src="//player.vimeo.com/video/${parsed}"></iframe>`;
    }
  }

  function _getVineEmbed(url) {
    const parsed = _vineParser(url);
    if (parsed) {
      return `<iframe class="vine-embed" src="https://vine.co/v/${parsed}/embed/simple" width="500" height="500" frameborder="0"></iframe><script async src="//platform.vine.co/static/scripts/embed.js" charset="utf-8"></script>`;
    }
  }

  function _getVideoGifvEmbed(url, vidWidth) {
    return `<video width="${vidWidth}" src="${url.replace(
      '.gifv',
      '.mp4'
    )}" controls autoplay><p>Your browser does not support this video format.</p></video>`;
  }

  function processFileUrl(url) {
    // Return URL as-is from server - no encoding needed for direct rendering
    // URLs should be used directly in img src, href, etc. without encoding
    // The server URLs are already correctly formatted
    return url;
  }

  service.processAttachment = async function processAttachment(src, totalArg, width, marginLeft = 0) {
    const attachment = angular.copy(src);
    const total = totalArg > 4 ? 4 : totalArg;

    attachment.url = processFileUrl(attachment.url);

    if (total === 1) {
      attachment.thumbWidth = width - marginLeft;
      attachment.thumbHeight = width / service.videoAspectRatio;
      attachment.thumbMode = 'lpad';
    } else {
      attachment.thumbWidth = width / total - marginLeft;
      attachment.thumbHeight = attachment.thumbWidth;
      attachment.thumbMode = 'fill';
    }

    attachment.thumbMode = 'lfill';

    attachment.thumbWidth = Math.floor(attachment.thumbWidth);
    attachment.thumbHeight = Math.floor(attachment.thumbHeight);

    if (attachment.mimetype === 'image/gif') {
      attachment.large = attachment.url;
      attachment.thumb = attachment.url;
      attachment.loadSize = _getImageAttachmentSize(attachment);
      attachment.loadEffectiveSize = _getEffectiveSize(attachment);
      return attachment;
    }

    if (attachment.mimetype.indexOf('image/') === 0) {
      const cdnEnabled = await _setImageResizeUrls(attachment);
      attachment.loadSize = _getImageAttachmentSize(attachment, 1000, cdnEnabled);
      attachment.loadEffectiveSize = _getEffectiveSize(attachment);
      return attachment;
    }

    if (attachment.mimetype === 'video/youtube') {
      const yid = _youtubeParser(attachment.url);
      if (yid) {
        attachment.thumb = _youtubeThumbUrl(yid.src);
        return attachment;
      }
    }

    if (attachment.mimetype === 'video/vimeo' || attachment.mimetype === 'video/vine') {
      attachment.thumb = _storedVideoThumb(attachment);
      return attachment;
    }

    const namePieces = attachment.url.split('/');
    attachment.name = decodeURI(namePieces[namePieces.length - 1]);

    if (attachment.transcoding === 2) {
      attachment.transcodedUrl = `${attachment.url}_t.mp4`;
      attachment.thumb = attachment.image;
    }

    return attachment;
  };

  service.getModalHtml = function getModalHtml(attachment, width, height) {
    const vidWidth = width || 858;
    const vidHeight = height || Math.floor(vidWidth / service.videoAspectRatio);

    let url = attachment.url;
    if (attachment.transcodedUrl) {
      url = attachment.transcodedUrl;
    }

    if (attachment.mimetype === 'video/mp4' || attachment.transcodedUrl) {
      return `<video width="${vidWidth}" height="${vidHeight}" src="${url}" controls autoplay><p>Your browser does not support this video format.</p></video>`;
    }

    if (attachment.mimetype.indexOf('image/') === 0) {
      // JPG / PNG: open a resized version, max 1000x1000 px
      // GIF: opens the original, as resizing takes waaay too long
      return '<img ng-src="{{ ctrl.attachment.large }}" />';
    }

    const getEmbed = {
      'video/youtube': _getYoutubeEmbed,
      'video/vimeo': _getVimeoEmbed,
      'video/vine': _getVineEmbed,
      'video/gifv': _getVideoGifvEmbed,
    };

    if (attachment.mimetype in getEmbed) {
      return getEmbed[attachment.mimetype](url, vidWidth, vidHeight);
    }
  };
}
